import { computed, InjectionKey } from 'vue';
import { RepositoryFactory, Repositories } from '@/repositories/RepositoryFactory';
import { Shop } from '@/repositories/shopRepository';
import { CouponConsumeSettings } from '@comsbi/common/src/types/models';
import { useData } from './useData';
import { ref } from 'vue';

const ShopRepository = RepositoryFactory.get('shop') as Repositories['shop'];

const SHOP_APP_UNFRIENDLY = //true; // DEBUG
  `${
    process.env.NODE_ENV === 'local'
      ? window.SHOP_APP_UNFRIENDLY || process.env.VUE_APP_SHOP_APP_UNFRIENDLY
      : window.SHOP_APP_UNFRIENDLY
  }` === 'true';

const COUPON_CUSTOMER_SETTINGS_JSON =
  process.env.NODE_ENV === 'local'
    ? window.COUPON_CONSUME_SETTINGS || process.env.VUE_APP_COUPON_CONSUME_SETTINGS
    : window.COUPON_CONSUME_SETTINGS;

export const SHOP_DETAILS_KEY = 'shopDetails';

export const useShop = () => {
  const data = useData();

  /**
   * 店舗アプリ親和性設定
   */
  const shopAppFriendly = computed(() => !SHOP_APP_UNFRIENDLY);

  /**
   * クーポン利用設定
   */
  const couponCustomerSettings = computed<CouponConsumeSettings>(() => {
    try {
      const settings: CouponConsumeSettings = JSON.parse(COUPON_CUSTOMER_SETTINGS_JSON);
      return settings;
    } catch {
      return {};
    }
  });

  /**
   * Shop詳細
   * @param shopId
   */
  const getDetail = async (shopId: string) => {
    return await ShopRepository.getDetail(shopId);
  };

  /**
   * Shop詳細
   * @param shopIds
   */
  const getDetails = async (shopIds: string[]) => {
    return await ShopRepository.getDetails(shopIds);
  };

  /**
   * 店舗情報を取得する（キャッシュ利用）
   */
  const getDetailCached = async (shopId: string): Promise<Shop | null> => {
    const cacheKey = `shop_${shopId}`;

    // キャッシュに存在する場合はそれを返す
    const cachedShop = data.get(cacheKey);
    if (cachedShop) {
      return cachedShop;
    }

    // キャッシュにない場合は取得してキャッシュに保存
    try {
      const shop = await getDetail(shopId);
      data.set(cacheKey, shop);
      return shop;
    } catch (e) {
      console.warn('Failed to get shop detail', e);
      return null;
    }
  };

  /**
   * 店舗情報のキャッシュクリア
   */
  const clearShopCache = (shopId?: string) => {
    if (shopId) {
      data.set(`shop_${shopId}`, null);
    } else {
      // すべての店舗キャッシュをクリア
      Object.keys(data.get('store') || {}).forEach(key => {
        if (key.startsWith('shop_')) {
          data.set(key, null);
        }
      });
    }
  };

  return {
    shopAppFriendly,
    couponCustomerSettings,
    getDetail,
    getDetails,

    getDetailCached,
    clearShopCache,
  };
};

type ShopType = ReturnType<typeof useShop>;
export const ShopKey: InjectionKey<ShopType> = Symbol('Shop');
